import { createSlice } from "@reduxjs/toolkit";
import { CampaignApplicationSlice, ScoreLevel, SortOptions } from "types/campaign/applications";
import { bookmarkApplciation, fetchApplication, fetchApplicationPosts, fetchCampaignApplications, revalidateLoadedApplicationPages } from "./thunk";
import { ERROR } from "types";
import { defaultApplicationFilters, defaultShortlistedFilters } from "Components/campaigns/applications/ApplicationFilters";
import { ApplicationsFilterTab } from "types/campaign/applications";
import { generateUniqueKey } from "utils/helpers";

const recalculateHasMore = (count: number, listLength: number) => count > listLength;

const defaultShortlistedFilterId = () =>
    generateUniqueKey({ ...defaultShortlistedFilters, bookmarked: 'y' }, ['page']);

const defaultApplicationsFilterId = () =>
    generateUniqueKey({ ...defaultApplicationFilters, bookmarked: 'n' }, ['page']);

const initialState: CampaignApplicationSlice = {
    loading: true,
    revalidating: false,
    error: null,
    shortlistedSummaryEpoch: {},
    campaigns: {},
    applicationLoading: false,
    applicationError: null,
    applied_filter_id: null,
    filtersApplications: defaultApplicationFilters,
    filtersShortlisted: defaultShortlistedFilters,
    posts: {},
    postsLoading: true
}

const campaignApplications = createSlice({
    name: 'campaignApplications',
    initialState,
    reducers: {
        updateApplicationFilters: (
            state,
            { payload }: { payload: { tab: ApplicationsFilterTab; filters: CampaignApplicationSlice['filtersApplications'] } }
        ) => {
            if (payload.tab === 'shortlisted') {
                state.filtersShortlisted = payload.filters;
            } else {
                state.filtersApplications = payload.filters;
            }
        },
        setApplicationSearchQeury: (
            state,
            action: { payload: { tab: ApplicationsFilterTab; q: string } }
        ) => {
            const filters =
                action.payload.tab === 'shortlisted' ? state.filtersShortlisted : state.filtersApplications;
            filters.q = action.payload.q;
            filters.page = 1;
        },
        updateApplicationStatus: (state, action) => {
            const { campaignId, status, applicationId }: { campaignId: string, applicationId: string, status: 'pending' | 'accepted' | 'rejected' | 'processing' } = action.payload;

            const campaignData = state.campaigns[campaignId] ? { ...state.campaigns[campaignId] } : null;
            if (campaignData) {
                const campaignApps = { ...campaignData.applications }
                if (campaignApps[applicationId]) {
                    campaignApps[applicationId] = {
                        ...campaignApps[applicationId],
                        application: {
                            ...campaignApps[applicationId].application,
                            status
                        }
                    }
                    campaignData.applications = campaignApps;
                }

                const appLists = { ...campaignData.filtered_lists }

                for (let filterId of Object.keys(appLists)) {
                    const list = [...appLists[filterId].list]

                    for (let i in list) {
                        if (list[i].uuid === applicationId) {
                            list[i] = {
                                ...list[i],
                                status
                            }
                        }
                    }

                    appLists[filterId] = {
                        ...appLists[filterId],
                        list
                    };
                }

                campaignData.filtered_lists = appLists;

                state.campaigns = {
                    ...state.campaigns,
                    [campaignId]: campaignData
                }

            }

        },
        invalidateCampaignApplicationLists: (
            state,
            { payload }: { payload: { campaignId: string; clearLists?: boolean } }
        ) => {
            const { campaignId, clearLists = false } = payload;
            const campaignData = state.campaigns[campaignId];
            if (!campaignData?.filtered_lists) return;

            const appLists = { ...campaignData.filtered_lists };
            for (const filterId of Object.keys(appLists)) {
                appLists[filterId] = {
                    ...appLists[filterId],
                    expired: true,
                    last_fetched_at: 0,
                    page: 1,
                    pendingRevalidateMaxPage: undefined,
                    ...(clearLists
                        ? {
                              list: [],
                              count: 0,
                              has_more: false,
                          }
                        : {}),
                };
            }

            state.campaigns[campaignId] = {
                ...campaignData,
                filtered_lists: appLists,
                ...(clearLists
                    ? {
                          totalsByBookmarked: {
                              ...(campaignData.totalsByBookmarked ?? {}),
                              y: 0,
                          },
                      }
                    : {}),
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCampaignApplications.pending, (state, action) => {
                if (!action.meta.arg.silent) {
                    state.loading = true;
                }
                state.error = null;
            })
            .addCase(fetchCampaignApplications.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                const filterId = action.payload.filterId;
                const campaignId = action.payload.campaignId;
                const existingData = state.campaigns?.[campaignId]?.filtered_lists?.[filterId]?.list ?? [];
                const finalList = action.payload.filters.page === 1 ? action.payload.campaignApplications.items : [...existingData, ...action.payload.campaignApplications.items];
                const bookmarkKey = action.payload.filters.bookmarked === 'y' ? 'y' : 'n';
                state.campaigns[action.payload.campaignId] = {
                    ...state.campaigns[action.payload.campaignId],
                    filtered_lists: {
                        ...(state.campaigns[campaignId]?.filtered_lists ?? {}),
                        [filterId]: {
                            list: finalList,
                            page: action.payload.filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: action.payload.campaignApplications.count > finalList.length,
                            filters: action.payload.filters,
                            count: action.payload.campaignApplications.count
                        }
                    },
                    total: action.payload.campaignApplications.total,
                    totalsByBookmarked: {
                        ...(state.campaigns[campaignId]?.totalsByBookmarked ?? {}),
                        [bookmarkKey]: action.payload.campaignApplications.count
                    }
                }
                if (!action.meta.arg.silent) {
                    state.applied_filter_id = filterId;
                }
            })
            .addCase(fetchCampaignApplications.rejected, (state, action) => {
                if (!action.meta.arg.silent) {
                    state.loading = false;
                }
                if (action.payload !== ERROR.CACHED)
                    state.error = action.payload || "Failed to fetch applications"
            })

            .addCase(revalidateLoadedApplicationPages.pending, (state) => {
                state.revalidating = true;
                state.error = null;
            })
            .addCase(revalidateLoadedApplicationPages.fulfilled, (state, action) => {
                state.revalidating = false;
                state.error = null;
                const { filterId, campaignId, list, filters, campaignApplications, maxPage } = action.payload;
                const bookmarkKey = filters.bookmarked === 'y' ? 'y' : 'n';

                state.campaigns[campaignId] = {
                    ...state.campaigns[campaignId],
                    filtered_lists: {
                        ...(state.campaigns[campaignId]?.filtered_lists ?? {}),
                        [filterId]: {
                            list,
                            page: maxPage,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: campaignApplications.count > list.length,
                            filters,
                            count: campaignApplications.count,
                            pendingRevalidateMaxPage: undefined,
                        },
                    },
                    total: campaignApplications.total,
                    totalsByBookmarked: {
                        ...(state.campaigns[campaignId]?.totalsByBookmarked ?? {}),
                        [bookmarkKey]: campaignApplications.count,
                    },
                };
                state.applied_filter_id = filterId;
            })
            .addCase(revalidateLoadedApplicationPages.rejected, (state, action) => {
                state.revalidating = false;
                state.error = action.payload || 'Failed to revalidate applications';
                const { campaignId, filterId } = action.meta.arg;
                const entry = state.campaigns[campaignId]?.filtered_lists?.[filterId];
                if (entry) {
                    state.campaigns[campaignId].filtered_lists[filterId] = {
                        ...entry,
                        pendingRevalidateMaxPage: undefined,
                        expired: true,
                        last_fetched_at: 0,
                    };
                }
            })

            .addCase(fetchApplication.pending, (state) => {
                state.applicationLoading = true;
                state.applicationError = null;
            })

            .addCase(fetchApplication.fulfilled, (state, action) => {
                state.applicationLoading = false;
                state.applicationError = null;

                const { campaignId, application, applicationId } = action.payload;

                const existingApplications = state.campaigns[campaignId]?.applications ?? {};

                state.campaigns[campaignId] = {
                    ...state.campaigns[campaignId],
                    applications: {
                        ...existingApplications,
                        [applicationId]: {
                            application: application,
                            last_fetched_at: Date.now()
                        }
                    }
                }
            })
            .addCase(fetchApplication.rejected, (state, { payload }) => {
                state.applicationLoading = false;
                state.applicationError = payload || 'Failed to fetch application';
            })
            .addCase(bookmarkApplciation.fulfilled, (state, { payload }) => {
                const { campaignId, applicationId, bookmarked } = payload;

                const campaignData = state.campaigns[campaignId] ? { ...state.campaigns[campaignId] } : null;
                if (campaignData) {
                    const campaignApps = { ...campaignData.applications }
                    if (campaignApps[applicationId]) {
                        campaignApps[applicationId] = {
                            ...campaignApps[applicationId],
                            application: {
                                ...campaignApps[applicationId].application,
                                bookmarked
                            }
                        }
                        campaignData.applications = campaignApps;
                    }

                    const appLists = { ...campaignData.filtered_lists }
                    const shortlistedDefaultId = defaultShortlistedFilterId();
                    const applicationsDefaultId = defaultApplicationsFilterId();

                    const adjustFilteredCount = (filterId: string, delta: number, markExpired: boolean) => {
                        const existing = appLists[filterId];
                        if (!existing) {
                            if (delta <= 0) return;
                            appLists[filterId] = {
                                list: [],
                                page: 1,
                                count: delta,
                                has_more: true,
                                expired: true,
                                last_fetched_at: 0,
                                filters: filterId === shortlistedDefaultId
                                    ? { ...defaultShortlistedFilters, page: 1 }
                                    : { ...defaultApplicationFilters, page: 1 },
                            };
                            return;
                        }
                        appLists[filterId] = {
                            ...existing,
                            count: Math.max(0, (existing.count ?? 0) + delta),
                            ...(markExpired ? { expired: true, last_fetched_at: 0 } : {}),
                        };
                    };

                    for (const filterId of Object.keys(appLists)) {
                        const entry = appLists[filterId];
                        const listBookmarked = entry.filters?.bookmarked === 'y' ? 'y' : 'n';
                        let list = [...entry.list];
                        const index = list.findIndex((item) => item.uuid === applicationId);
                        const isTargetTab = listBookmarked === bookmarked;
                        const isRemoval = index !== -1 && !isTargetTab;

                        if (index !== -1) {
                            if (isTargetTab) {
                                list[index] = { ...list[index], bookmarked };
                            } else {
                                list = list.filter((item) => item.uuid !== applicationId);
                            }
                        }

                        const count = isRemoval
                            ? Math.max(0, (entry.count ?? list.length + 1) - 1)
                            : entry.count ?? list.length;

                        const has_more = recalculateHasMore(count, list.length);

                        // Target tab (e.g. Shortlisted after shortlist): invalidate so next view refetches.
                        if (isTargetTab) {
                            appLists[filterId] = {
                                ...entry,
                                list,
                                count,
                                has_more,
                                expired: true,
                                last_fetched_at: 0,
                            };
                            continue;
                        }

                        // Source tab: page 1 → simple refetch; pages 2+ → revalidate all loaded pages (offset shift fix).
                        const onlyFirstPageLoaded = entry.page <= 1;

                        appLists[filterId] = {
                            ...entry,
                            list,
                            count,
                            has_more,
                            ...(isRemoval && onlyFirstPageLoaded
                                ? {
                                      expired: true,
                                      last_fetched_at: 0,
                                      page: 1,
                                      pendingRevalidateMaxPage: undefined,
                                  }
                                : {}),
                            ...(isRemoval && !onlyFirstPageLoaded
                                ? { pendingRevalidateMaxPage: entry.page }
                                : {}),
                        };
                    }

                    if (bookmarked === 'y') {
                        adjustFilteredCount(shortlistedDefaultId, 1, true);
                        adjustFilteredCount(applicationsDefaultId, -1, false);
                    } else {
                        adjustFilteredCount(shortlistedDefaultId, -1, false);
                        adjustFilteredCount(applicationsDefaultId, 1, true);
                    }

                    campaignData.filtered_lists = appLists;

                    state.campaigns = {
                        ...state.campaigns,
                        [campaignId]: campaignData
                    }

                }

                state.shortlistedSummaryEpoch[campaignId] =
                    (state.shortlistedSummaryEpoch[campaignId] ?? 0) + 1;
            })
            .addCase(fetchApplicationPosts.pending, (state) => {
                state.postsLoading = true;
            })
            .addCase(fetchApplicationPosts.fulfilled, (state, {payload}) => {
                const { posts, campaignId, influencerId } = payload;

                state.posts[campaignId] = {
                    ...(state.posts[campaignId] ?? {}),
                    [influencerId] : {
                        ...posts,
                        last_fetched_at: Date.now()
                    }
                }
                state.postsLoading = false;

            })
            .addCase(fetchApplicationPosts.rejected, (state) => {
                state.postsLoading = false;
            })

    }
})

export const {
    updateApplicationFilters,
    updateApplicationStatus,
    setApplicationSearchQeury,
    invalidateCampaignApplicationLists,
} = campaignApplications.actions;

export default campaignApplications.reducer;